import React from 'react';
import { Text } from '@fluentui/react-components';
import { DocumentArrowDownRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { useFilmingApprovalPanelStyles } from './FilmingApprovalPanel.styles';

export interface FilmingApprovalPanelProps {
  filmingFormUrl?: string;
  gsocEmail?: string;
  regionalSecurityAdvisorEmail?: string;
  siteContactEmail?: string[];
}

export const FilmingApprovalPanel: React.FC<FilmingApprovalPanelProps> = ({
  filmingFormUrl,
  gsocEmail,
  regionalSecurityAdvisorEmail,
  siteContactEmail,
}) => {
  const { t } = useTranslation('bookingForm');
  const styles = useFilmingApprovalPanelStyles();

  const hasAnyContact =
    gsocEmail || regionalSecurityAdvisorEmail || (siteContactEmail && siteContactEmail.length > 0);

  if (!filmingFormUrl && !hasAnyContact) return null;

  return (
    <div
      className={styles.container}
      role="region"
      aria-label={t('bookingForm.filmingApproval.ariaLabel')}
    >
      <Text className={styles.title}>{t('bookingForm.filmingApproval.title')}</Text>
      <Text className={styles.description}>{t('bookingForm.filmingApproval.description')}</Text>

      {filmingFormUrl && (
        <BasicButton
          as="a"
          href={filmingFormUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="primary"
          icon={<DocumentArrowDownRegular />}
          className={styles.downloadButton}
        >
          {t('bookingForm.filmingApproval.downloadForm')}
        </BasicButton>
      )}

      {gsocEmail && (
        <div className={styles.contactLine}>
          <Text className={styles.contactLabel}>{t('bookingForm.filmingApproval.gsocTeam')}:</Text>
          <a href={`mailto:${gsocEmail}`} className={styles.contactLink}>
            {gsocEmail}
          </a>
        </div>
      )}

      {regionalSecurityAdvisorEmail && (
        <div className={styles.contactLine}>
          <Text className={styles.contactLabel}>{t('bookingForm.filmingApproval.rsaTeam')}:</Text>
          <a href={`mailto:${regionalSecurityAdvisorEmail}`} className={styles.contactLink}>
            {regionalSecurityAdvisorEmail}
          </a>
        </div>
      )}

      {siteContactEmail && siteContactEmail.length > 0 && (
        <div className={styles.contactLine}>
          <Text className={styles.contactLabel}>
            {t('bookingForm.filmingApproval.facilitiesTeam')}:
          </Text>
          <span>
            {siteContactEmail.map((email, idx) => (
              <React.Fragment key={email}>
                {idx > 0 && ', '}
                <a href={`mailto:${email}`} className={styles.contactLink}>
                  {email}
                </a>
              </React.Fragment>
            ))}
          </span>
        </div>
      )}
    </div>
  );
};

FilmingApprovalPanel.displayName = 'FilmingApprovalPanel';
